// frontend/src/pages/TrainingAdminPage.jsx
//
// Capacitación · Solo Arsenal: constructor de cursos, asignaciones y progreso.
//
// La elegibilidad se define con REGLAS, no asignando usuario por usuario: marcar
// "Todas las empresas" crea UNA regla que cubre también a quien se registre
// mañana. Por eso el selector ofrece alcance, no una lista de cientos de filas.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  Grid, IconButton, InputLabel, LinearProgress, MenuItem, Paper, Select, Stack,
  Switch, FormControlLabel, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  MenuBook as CourseIcon,
  Public as GlobalIcon,
  Publish as PublishIcon,
  DragIndicator as DragIcon,
  Refresh as RefreshIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { api } from '../services/api';

const TIPOS_BLOQUE = [
  { value: 'text', label: 'Texto' },
  { value: 'video', label: 'Vídeo (URL)' },
  { value: 'image', label: 'Imagen (URL)' },
  { value: 'file', label: 'Archivo' },
  { value: 'heading', label: 'Título' },
  { value: 'confirmation', label: 'Confirmación' },
  { value: 'link', label: 'Enlace' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'callout', label: 'Aviso' },
];

const TrainingAdminPage = () => {
  const [tab, setTab] = useState(0);
  const [cursos, setCursos] = useState([]);
  const [stats, setStats] = useState(null);
  const [progreso, setProgreso] = useState({ items: [], total: 0 });
  const [cargando, setCargando] = useState(true);
  const [msg, setMsg] = useState(null);

  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [nuevo, setNuevo] = useState({ title: '', summary: '', is_mandatory: false, sequential: false });
  const [editando, setEditando] = useState(null);   // curso en el constructor

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      api.training.admin.list().then((r) => setCursos(r.data?.items || [])).catch(() => {}),
      api.training.admin.stats().then((r) => setStats(r.data)).catch(() => {}),
      api.training.admin.progress({ limit: 100 }).then((r) => setProgreso(r.data)).catch(() => {}),
    ]).finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const aviso = (tipo, texto) => setMsg({ tipo, texto });
  const fallo = (err, alt) => aviso('error', err?.response?.data?.detail || alt);

  const crear = () => {
    if (!nuevo.title.trim()) return;
    api.training.admin.create(nuevo)
      .then(() => { setNuevoAbierto(false); setNuevo({ title: '', summary: '', is_mandatory: false, sequential: false }); cargar(); aviso('success', 'Curso creado'); })
      .catch((e) => fallo(e, 'No se pudo crear el curso'));
  };

  const publicar = (curso, publish) => {
    api.training.admin.publish(curso.id, publish)
      .then(() => { cargar(); aviso('success', publish ? 'Curso publicado' : 'Curso despublicado'); })
      // El backend impide publicar sin lecciones o sin asignación: se muestra
      // su motivo tal cual, que es accionable.
      .catch((e) => fallo(e, 'No se pudo publicar'));
  };

  const archivar = (curso) => {
    if (!window.confirm(`¿Archivar «${curso.title}»? Dejará de verse, pero se conserva el historial.`)) return;
    api.training.admin.archive(curso.id)
      .then(() => { cargar(); aviso('success', 'Curso archivado'); })
      .catch((e) => fallo(e, 'No se pudo archivar'));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <CourseIcon color="primary" />
            <Typography variant="h5" fontWeight={800}>Capacitación</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Cursos, contenido, asignaciones y progreso — Solo Arsenal.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={cargar}>Actualizar</Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => setNuevoAbierto(true)}>Nuevo curso</Button>
        </Stack>
      </Stack>

      {msg && <Alert severity={msg.tipo} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.texto}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            ['Cursos publicados', stats.published_courses],
            ['Cursos iniciados', stats.started],
            ['Completados', stats.completed],
            ['Tasa de finalización', `${stats.completion_rate}%`],
            ['Progreso promedio', `${stats.average_percent}%`],
          ].map(([label, valor]) => (
            <Grid item xs={6} sm={4} md={2.4} key={label}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="h6" fontWeight={800}>{valor}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Cursos" />
        <Tab label="Progreso" />
      </Tabs>

      {cargando && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {!cargando && tab === 0 && (
        cursos.length === 0 ? (
          <Card variant="outlined"><CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700}>Aún no has creado cursos</Typography>
            <Typography variant="body2" color="text.secondary">
              Crea el primero y añádele secciones, lecciones y contenido.
            </Typography>
          </CardContent></Card>
        ) : (
          <Grid container spacing={2}>
            {cursos.map((c) => (
              <Grid item xs={12} md={6} lg={4} key={c.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="small" label={c.status}
                        color={c.status === 'published' ? 'success' : c.status === 'archived' ? 'default' : 'warning'} />
                      {c.is_mandatory && <Chip size="small" color="error" label="Obligatorio" />}
                      {c.sequential && <Chip size="small" variant="outlined" label="Secuencial" />}
                    </Stack>
                    <Typography variant="subtitle1" fontWeight={700}>{c.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.slug}</Typography>
                    {c.summary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{c.summary}</Typography>
                    )}
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => setEditando(c)}>
                        Contenido
                      </Button>
                      <Button size="small" startIcon={<PublishIcon />}
                        onClick={() => publicar(c, c.status !== 'published')}>
                        {c.status === 'published' ? 'Despublicar' : 'Publicar'}
                      </Button>
                      <Tooltip title="Archivar">
                        <IconButton size="small" onClick={() => archivar(c)}><ArchiveIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      )}

      {!cargando && tab === 1 && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Usuario', 'Empresa', 'Curso', 'Progreso', 'Estado', 'Última actividad'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {progreso.items.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nadie ha empezado un curso todavía.</Typography>
                </TableCell></TableRow>
              )}
              {progreso.items.map((p, i) => (
                <TableRow key={`${p.user_id}-${p.course_id}-${i}`} hover>
                  <TableCell>
                    <Typography variant="body2">{p.user_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.user_email}</Typography>
                  </TableCell>
                  <TableCell>{p.company_name}</TableCell>
                  <TableCell>{p.course_title}</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <LinearProgress variant="determinate" value={p.percent}
                      sx={{ height: 6, borderRadius: 3 }} />
                    <Typography variant="caption">{p.percent}%</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={p.status}
                      color={p.status === 'completed' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {p.last_activity_at ? new Date(p.last_activity_at).toLocaleString('es-MX') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Crear curso */}
      <Dialog open={nuevoAbierto} onClose={() => setNuevoAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo curso</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título" fullWidth value={nuevo.title} autoFocus
              onChange={(e) => setNuevo({ ...nuevo, title: e.target.value })} />
            <TextField label="Resumen" fullWidth multiline rows={2} value={nuevo.summary}
              onChange={(e) => setNuevo({ ...nuevo, summary: e.target.value })} />
            <FormControlLabel label="Obligatorio"
              control={<Switch checked={nuevo.is_mandatory}
                onChange={(e) => setNuevo({ ...nuevo, is_mandatory: e.target.checked })} />} />
            <FormControlLabel label="Secuencial (hay que completar cada lección en orden)"
              control={<Switch checked={nuevo.sequential}
                onChange={(e) => setNuevo({ ...nuevo, sequential: e.target.checked })} />} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevoAbierto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crear} disabled={!nuevo.title.trim()}>Crear</Button>
        </DialogActions>
      </Dialog>

      {editando && (
        <CourseBuilder curso={editando} onClose={() => { setEditando(null); cargar(); }}
          onError={(e, alt) => fallo(e, alt)} onOk={(t) => aviso('success', t)} />
      )}
    </Box>
  );
};

// ── Constructor: secciones, lecciones, bloques y asignaciones ──
const CourseBuilder = ({ curso, onClose, onError, onOk }) => {
  const [detalle, setDetalle] = useState(null);
  const [asignaciones, setAsignaciones] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nuevoBloque, setNuevoBloque] = useState({ lessonId: null, type: 'text', value: '', title: '' });
  const [validacion, setValidacion] = useState(null);
  const [subiendo, setSubiendo] = useState(false);

  const recargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      api.training.course(curso.id).then((r) => setDetalle(r.data)).catch(() => setDetalle(null)),
      api.training.admin.assignments(curso.id).then((r) => setAsignaciones(r.data?.items || [])).catch(() => {}),
      api.companies?.getAll ? api.companies.getAll().then((r) => setEmpresas(r.data?.items || r.data || [])).catch(() => {}) : Promise.resolve(),
    ]).finally(() => setCargando(false));
  }, [curso.id]);

  useEffect(() => { recargar(); }, [recargar]);

  const addSection = () => {
    const t = window.prompt('Título de la sección');
    if (!t) return;
    api.training.admin.addSection(curso.id, { title: t }).then(recargar)
      .catch((e) => onError(e, 'No se pudo crear la sección'));
  };

  const addLesson = (sectionId) => {
    const t = window.prompt('Título de la lección');
    if (!t) return;
    api.training.admin.addLesson(sectionId, { title: t }).then(recargar)
      .catch((e) => onError(e, 'No se pudo crear la lección'));
  };

  const validarVideo = (url) => {
    if (!url) return;
    api.training.admin.validateVideo(url)
      .then((r) => setValidacion(r.data))
      .catch(() => setValidacion(null));
  };

  const addBlock = () => {
    const { lessonId, type, value, title } = nuevoBloque;
    if (!lessonId) return;
    const payload = type === 'text' ? { text: value }
      : type === 'checklist' ? { items: value.split('\n').map((s) => s.trim()).filter(Boolean) }
        : type === 'callout' ? { text: value, severity: 'info' }
          : { url: value };
    api.training.admin.addBlock(lessonId, { block_type: type, title: title || null, payload })
      .then(() => { setNuevoBloque({ lessonId: null, type: 'text', value: '', title: '' }); setValidacion(null); recargar(); onOk('Bloque añadido'); })
      .catch((e) => onError(e, 'No se pudo añadir el bloque'));
  };

  const alSoltar = (resultado) => {
    const { source, destination, type, draggableId } = resultado;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'seccion') {
      const ids = (detalle.sections || []).map((x) => x.id);
      ids.splice(source.index, 1);
      ids.splice(destination.index, 0, draggableId);
      // Optimista: se pinta ya y se confirma contra el backend. Si falla, se
      // recarga para no dejar en pantalla un orden que no se guardó.
      setDetalle((prev) => ({
        ...prev,
        sections: ids.map((id) => prev.sections.find((x) => x.id === id)),
      }));
      api.training.admin.reorderSections(curso.id, ids)
        .then(() => onOk('Orden guardado'))
        .catch((e) => { onError(e, 'No se pudo guardar el orden'); recargar(); });
      return;
    }

    // Las lecciones solo se reordenan dentro de su propia sección: moverlas
    // entre secciones exigiría cambiar su `section_id`, que es otra operación.
    if (source.droppableId !== destination.droppableId) {
      onOk('Por ahora las lecciones se reordenan dentro de su misma sección');
      return;
    }
    const seccion = detalle.sections.find((x) => x.id === source.droppableId);
    const ids = seccion.lessons.map((x) => x.id);
    ids.splice(source.index, 1);
    ids.splice(destination.index, 0, draggableId);
    setDetalle((prev) => ({
      ...prev,
      sections: prev.sections.map((x) => (x.id !== seccion.id ? x : {
        ...x, lessons: ids.map((id) => x.lessons.find((l) => l.id === id)),
      })),
    }));
    api.training.admin.reorderLessons(seccion.id, ids)
      .then(() => onOk('Orden guardado'))
      .catch((e) => { onError(e, 'No se pudo guardar el orden'); recargar(); });
  };

  const subirArchivo = (evento) => {
    const archivo = evento.target.files?.[0];
    if (!archivo) return;
    const datos = new FormData();
    datos.append('file', archivo);
    setSubiendo(true);
    api.training.admin.upload(datos)
      .then((r) => {
        setNuevoBloque((prev) => ({ ...prev, value: r.data.url, title: prev.title || r.data.filename }));
        onOk(`Archivo subido (${r.data.size_label})`);
      })
      .catch((e) => onError(e, 'No se pudo subir el archivo'))
      .finally(() => setSubiendo(false));
  };

  const addAssignment = (scope, companyId) => {
    api.training.admin.addAssignment(curso.id, { scope, company_id: companyId || null })
      .then(() => { recargar(); onOk(scope === 'global' ? 'Disponible para todas las empresas' : 'Empresa asignada'); })
      .catch((e) => onError(e, 'No se pudo asignar'));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>{curso.title}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {cargando && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /></Box>}

        {!cargando && (
          <Stack spacing={3}>
            {/* Asignaciones */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                ¿Quién puede verlo?
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                {asignaciones.map((a) => (
                  <Chip key={a.id} size="small"
                    icon={a.scope === 'global' ? <GlobalIcon /> : undefined}
                    label={a.scope === 'global' ? 'Todas las empresas'
                      : a.scope === 'company' ? a.company_name
                        : a.scope === 'user' ? a.user_name : a.role}
                    onDelete={() => api.training.admin.removeAssignment(a.id).then(recargar)
                      .catch((e) => onError(e, 'No se pudo quitar'))} />
                ))}
                {asignaciones.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Sin asignar: nadie lo verá y no se podrá publicar.
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" startIcon={<GlobalIcon />}
                  onClick={() => addAssignment('global')}>
                  Todas las empresas
                </Button>
                {empresas.length > 0 && (
                  <Autocomplete size="small" sx={{ minWidth: 240 }} options={empresas}
                    getOptionLabel={(o) => o.company_name || o.name || ''}
                    onChange={(_, v) => v && addAssignment('company', v.id)}
                    renderInput={(pr) => <TextField {...pr} label="Añadir empresa" />} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                «Todas las empresas» es una sola regla: cubre también a los usuarios que se registren después.
              </Typography>
            </Box>

            <Divider />

            {/* Estructura */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>Contenido</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addSection}>Sección</Button>
              </Stack>

              {(detalle?.sections || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Sin secciones todavía. Crea la primera para añadir lecciones.
                </Typography>
              )}

              {/* Arrastrar para reordenar. El orden se persiste en el backend en
                  cuanto se suelta: no hay un botón "guardar orden" que olvidar. */}
              <DragDropContext onDragEnd={alSoltar}>
                <Droppable droppableId="secciones" type="seccion">
                  {(dropSec) => (
                    <Box ref={dropSec.innerRef} {...dropSec.droppableProps}>
                      {(detalle?.sections || []).map((s, i) => (
                        <Draggable key={s.id} draggableId={s.id} index={i}>
                          {(dragSec, snapSec) => (
                            <Paper variant="outlined"
                              ref={dragSec.innerRef} {...dragSec.draggableProps}
                              sx={{ p: 1.5, mb: 1.5, bgcolor: snapSec.isDragging ? 'action.hover' : undefined }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                                  <Box {...dragSec.dragHandleProps} sx={{ display: 'flex', cursor: 'grab' }}>
                                    <DragIcon fontSize="small" color="disabled" />
                                  </Box>
                                  <Typography variant="subtitle2" fontWeight={700} noWrap>{s.title}</Typography>
                                </Stack>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => addLesson(s.id)}>
                                  Lección
                                </Button>
                              </Stack>

                              <Droppable droppableId={s.id} type="leccion">
                                {(dropLec) => (
                                  <Box ref={dropLec.innerRef} {...dropLec.droppableProps} sx={{ mt: 0.5 }}>
                                    {s.lessons.map((l, j) => (
                                      <Draggable key={l.id} draggableId={l.id} index={j}>
                                        {(dragLec, snapLec) => (
                                          <Box ref={dragLec.innerRef} {...dragLec.draggableProps}
                                            sx={{
                                              pl: 1, py: 0.75, borderTop: '1px solid', borderColor: 'divider',
                                              bgcolor: snapLec.isDragging ? 'action.selected' : undefined,
                                            }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                                                <Box {...dragLec.dragHandleProps} sx={{ display: 'flex', cursor: 'grab' }}>
                                                  <DragIcon fontSize="small" color="disabled" />
                                                </Box>
                                                <Typography variant="body2" noWrap>{l.title}</Typography>
                                              </Stack>
                                              <Button size="small"
                                                onClick={() => setNuevoBloque({ lessonId: l.id, type: 'text', value: '', title: '' })}>
                                                Añadir bloque
                                              </Button>
                                            </Stack>
                                          </Box>
                                        )}
                                      </Draggable>
                                    ))}
                                    {dropLec.placeholder}
                                  </Box>
                                )}
                              </Droppable>
                            </Paper>
                          )}
                        </Draggable>
                      ))}
                      {dropSec.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>

            {/* Alta de bloque */}
            {nuevoBloque.lessonId && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Nuevo bloque</Typography>
                <Stack spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select label="Tipo" value={nuevoBloque.type}
                      onChange={(e) => { setNuevoBloque({ ...nuevoBloque, type: e.target.value, value: '' }); setValidacion(null); }}>
                      {TIPOS_BLOQUE.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Título (opcional)" value={nuevoBloque.title}
                    onChange={(e) => setNuevoBloque({ ...nuevoBloque, title: e.target.value })} />
                  <TextField size="small" multiline={['text', 'checklist', 'callout'].includes(nuevoBloque.type)}
                    rows={3}
                    label={nuevoBloque.type === 'checklist' ? 'Un ítem por línea'
                      : ['text', 'callout'].includes(nuevoBloque.type) ? 'Contenido' : 'URL'}
                    value={nuevoBloque.value}
                    onChange={(e) => setNuevoBloque({ ...nuevoBloque, value: e.target.value })}
                    onBlur={() => nuevoBloque.type === 'video' && validarVideo(nuevoBloque.value)} />

                  {validacion && (
                    <Alert severity={validacion.embeddable ? 'success' : 'warning'}>
                      {validacion.embeddable
                        ? `Se reproducirá aquí (${validacion.provider}).`
                        : `No se puede incrustar: se mostrará como enlace. ${validacion.reason || ''}`}
                    </Alert>
                  )}

                  {['file', 'image', 'document'].includes(nuevoBloque.type) && (
                    <Button component="label" size="small" variant="outlined"
                      startIcon={<UploadIcon />} disabled={subiendo}>
                      {subiendo ? 'Subiendo…' : 'Subir archivo'}
                      <input hidden type="file" onChange={subirArchivo} />
                    </Button>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" size="small" onClick={addBlock}
                      disabled={!nuevoBloque.value.trim()}>Añadir</Button>
                    <Button size="small" onClick={() => { setNuevoBloque({ lessonId: null, type: 'text', value: '', title: '' }); setValidacion(null); }}>
                      Cancelar
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingAdminPage;
